import { useEffect } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import {
  getGoogleMapsApiKey,
  hasGoogleMapsApiKey,
  REQUIRED_GOOGLE_MAPS_LIBRARIES,
} from "@/lib/GoogleMaps/config";
import MantineProviderWrapper from "./MantineProviderWrapper";
import QueryClientProviderWrapper from "./QueryClientProviderWrapper";

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    let lastPointerTarget: EventTarget | null = null;

    const onPointerDown = (e: PointerEvent) => {
      lastPointerTarget = e.target;
    };

    const onFocusIn = (e: FocusEvent) => {
      if (e.target === lastPointerTarget) return;
      const target = e.target as HTMLElement;
      if (!target) return;
      if (!["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if ((target as HTMLInputElement).type === "hidden") return;

      const rect = target.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn, true);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
    };
  }, []);

  /*
   * Maps is loaded once, here, rather than by whichever screen happens to need it first.
   * `lib/GoogleMaps/autocomplete.ts` used to inject its own <script> tag as a safety net for
   * screens sitting outside an APIProvider, which meant two loaders racing: vis.gl appends its
   * script behind an await, so there is a window where `window.google.maps` exists, `.places`
   * does not, and no tag is in the DOM - the injector's guard missed it and loaded a second copy
   * of the API, which Google does not support.
   *
   * The five APIProviders around individual screens still work and are left alone: vis.gl's
   * loader is idempotent ("abort if the API is already loading or has been loaded"), so a nested
   * one is a no-op. This provider loads first and asks for every library the app declares, so it
   * is the one that wins.
   */
  const mapsBody = (
    <MantineProviderWrapper>
      <QueryClientProviderWrapper>{children}</QueryClientProviderWrapper>
    </MantineProviderWrapper>
  );

  /* No key configured (local dev, CI): render without the provider rather than mounting one that
   * can only fail. Autocomplete degrades to plain text entry, which is what it did before. */
  if (!hasGoogleMapsApiKey()) return mapsBody;

  return (
    <APIProvider
      apiKey={getGoogleMapsApiKey()}
      libraries={REQUIRED_GOOGLE_MAPS_LIBRARIES}
      onError={(error) => console.error("Google Maps API error:", error)}
    >
      {mapsBody}
    </APIProvider>
  );
};
